import Pusher from "pusher-js";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getBaseUrl, isLoggedInUser, isPatientChatMessage, requestNotificationPermission, showBrowserNotification, removeHtmlTags, buildNotificationPayload, storeNotificationPayload } from "../helpers/utility";
import baseApiHelper, { updateStoreThroughPusher } from "../services/baseApiHelper";
import { chatApi, useGetNursingHomeLocationsQuery } from "../services/chatService";
import { locationsApi } from "../services/location_services";
import { useGroupChatContext } from "./groupchatContext";
const PusherContext = React.createContext();

const usePusherContext = () => {
  return useContext(PusherContext);
}

const CLEAR_MESSAGE_COUNT_DELAY = 2000;
const SUPPRESS_UNREAD_EVENT_TYPES = ['appointment', 'request_appointment'];
const notifyTypes = ['checkin', 'checkout', 'sms_enabled', 'sms_disabled', 'call_in_app_enabled', 'call_in_app_disabled', 'call_phone_enabled', 'call_phone_disabled']

const PusherWrapper = ({ children }) => {

    const pusherRef = useRef();
    const subscribedChannelNamesRef = useRef([]);

    const questionUpdateTimeout = useRef(null);
    const acknowledgeTimeout = useRef(null);
    const clearMessageCountTimeout = useRef(null);
    const pendingClearMessageCountPayload = useRef(null);

    const [nursingHomeLocations, setNursingHomeLocations] = useState([]);
    const [channelNames, setChannelNames] = useState([]);
    const [telemedPusherData, setTelemedPusherData] = useState(null);
    const [pusherData, setPusherData] = useState(null);

    if(!getBaseUrl()?.pusher_settings?.key){
        localStorage.removeItem('base_url');
    }

    const getPusher = () => {
        const pusherSettings = getBaseUrl()?.pusher_settings;
        if (!pusherSettings?.key) {
            return null;
        }

        if (!pusherRef.current) {
            pusherRef.current = new Pusher(pusherSettings.key, {
                cluster: pusherSettings.cluster,
            });
        }

        return pusherRef.current;
    }

    useEffect(() => {
        return () => {
            pusherRef.current?.disconnect();
            pusherRef.current = null;
            subscribedChannelNamesRef.current = [];
            if (clearMessageCountTimeout.current) {
                clearTimeout(clearMessageCountTimeout.current);
            }
        };
    }, []);


    const groupChatContext = useGroupChatContext();

    const {authData, setAuthData, selectedChat, setSelectedChat, selectedLocations, selectedChatTab, setMessages} = useMemo(() => ({
        authData: groupChatContext.authData,
        setAuthData: groupChatContext.setAuthData,
        selectedChat: groupChatContext.selectedChat,
        setSelectedChat: groupChatContext.setSelectedChat,
        selectedLocations: groupChatContext.selectedLocations,
        selectedChatTab: groupChatContext.selectedChatTab,
        setMessages: groupChatContext.setMessages
    }));

    const selectedChatRef = useRef(selectedChat)
    const selectedChatTabRef = useRef(selectedChatTab)
    const authDataRef = useRef(authData)

    useEffect(()=>{
        selectedChatRef.current = selectedChat
    },[selectedChat])

    useEffect(()=>{
        selectedChatTabRef.current = selectedChatTab
    },[selectedChatTab])

    useEffect(()=>{
        authDataRef.current = authData
    },[authData])

    const [getNursingHomeLocations] = chatApi.endpoints.getNursingHomeLocations.useLazyQuery();
    const [getNurseLocations] = locationsApi.endpoints.getNurseLocations.useLazyQuery();
    const [clearMessageCount] = chatApi.endpoints.clearMessageCount.useMutation();

    const fetchNursingLocations = async() => {
        const response = await getNursingHomeLocations();
        setNursingHomeLocations(response.data);
    }

    useEffect(()=>{
        if(authData && Object.keys(authData).length && !authData?.is_provider){
            fetchNursingLocations()
        }
        if(authData?.is_provider){
            setChannelNames([authData?.uuid].filter(Boolean));
        }
        
        // Request notification permission when user is authenticated
        if(authData && Object.keys(authData).length){
            requestNotificationPermission();
        }
    }, [authData])

    useEffect(()=>{
        if(!authData?.is_provider && nursingHomeLocations?.length){
            let tempChannelNames = [];
            nursingHomeLocations.forEach((location)=>{
                if(location?.uuid){
                    tempChannelNames.push(location.uuid)
                }
            })
            setChannelNames([...new Set(tempChannelNames)])
        }
    }, [nursingHomeLocations])


    const onUpdatePatientLocation = (data) => {
        if(selectedChatTab == 'patient_chats'){
            // if the patient chat is open and the location is changed, then we need to close the chat
            if(selectedChatRef.current?.pid == data.pid){
                setSelectedChat({});
            }
            baseApiHelper().updatePatientChatCacheThroughPusher(data, selectedLocations);
        }
    }

    // Show notification immediately when Pusher event arrives (not in useEffect)
    // React defers re-renders when tab is backgrounded, so useEffect runs too late
    const tryShowNotificationForMessage = (data) => {
        if (!data?.message) return;
        if (isLoggedInUser(authDataRef.current, data)) return;
        const currentChat = selectedChatRef.current;
        const currentLocationId = currentChat?.location_id || currentChat?.location?.id;
        const dataLocationId = data?.location_id || data?.location?.id;
        const isCurrentChatOpened = selectedChatTabRef.current === 'location_chats'
            ? currentLocationId == dataLocationId
            : data.pid == currentChat?.pid;
        if (isCurrentChatOpened) return;

        const messageText = data.message?.text || "";
        const cleanMessageText = removeHtmlTags(messageText).substring(0, 100);
        const senderName = data.sender;
        const notificationTitle = isPatientChatMessage(data)
            ? `New message from ${senderName}`
            : `New message from ${data.location?.name || "Location"}`;

        const notificationPayload = buildNotificationPayload(data);
        storeNotificationPayload(notificationPayload);
        showBrowserNotification(notificationTitle, {
            body: cleanMessageText || "You have a new message",
            tag: `message-${data.id}`,
            requireInteraction: false,
            data: notificationPayload,
        });
    }

    const isMessageForCurrentChat = (data) => {
        const currentChat = selectedChatRef.current;
        if (!data || !currentChat) {
            return false;
        }
        const currentLocationId = currentChat?.location_id || currentChat?.location?.id;
        const dataLocationId = data?.location_id || data?.location?.id;
        if (selectedChatTabRef.current === 'location_chats') {
            return !isPatientChatMessage(data) && `${currentLocationId}` === `${dataLocationId}`;
        }
        return isPatientChatMessage(data) && `${data.pid}` === `${currentChat?.pid}`;
    }

    const shouldSuppressUnreadCount = (data) => {
        return SUPPRESS_UNREAD_EVENT_TYPES.includes(data?.message?.event_type);
    }

    const clearOpenChatMessageCount = (data) => {
        if (!data?.message || !isMessageForCurrentChat(data)) {
            return;
        }

        const currentChat = selectedChatRef.current;
        const locationId = data?.location_id || data?.location?.id || currentChat?.location_id || currentChat?.location?.id;
        if (!locationId) {
            return;
        }

        const payload = selectedChatTabRef.current === 'location_chats'
            ? { location_id: locationId }
            : { pid: data?.pid || currentChat?.pid, location_id: locationId };

        if (selectedChatTabRef.current !== 'location_chats' && !payload.pid) {
            return;
        }

        pendingClearMessageCountPayload.current = payload;
        if (clearMessageCountTimeout.current) {
            clearTimeout(clearMessageCountTimeout.current);
        }

        clearMessageCountTimeout.current = setTimeout(() => {
            const latestPayload = pendingClearMessageCountPayload.current;
            pendingClearMessageCountPayload.current = null;
            clearMessageCountTimeout.current = null;

            if (!latestPayload) {
                return;
            }

            clearMessageCount(latestPayload)
                .unwrap()
                .then(() => {
                    baseApiHelper().invalidateTags(["UnreadMessageCount"]);
                })
                .catch((error) => {
                    console.error("Error clearing open chat message count:", error);
                });
        }, CLEAR_MESSAGE_COUNT_DELAY);
    }

    const updateOpenThreadWithPusherMessage = (data) => {
        if (!data?.message || !isMessageForCurrentChat(data)) {
            return;
        }
        if (isLoggedInUser(authDataRef.current, data)) {
            return;
        }
        setMessages((prevMessages) => {
            const messageExists = prevMessages?.data?.some((message) => message?.id && `${message.id}` === `${data.id}`);
            if (messageExists) {
                return prevMessages;
            }
            return {
                ...prevMessages,
                data: [data, ...(prevMessages?.data || [])],
            };
        });
    }


    const updateStoredAuthData = (updates = {}) => {
        const currentUser = authDataRef.current || {};
        const nextAuthData = { ...currentUser, ...updates };
        localStorage.setItem('auth', JSON.stringify(nextAuthData));
        authDataRef.current = nextAuthData;
        setAuthData(nextAuthData);
    }

    const syncFacilityAdminStatusUpdate = async (data) => {
        const currentUser = authDataRef.current;
        const isAdminRevoked = data?.is_admin === 0 || data?.is_admin === false;
        const isCurrentNurse = !currentUser?.is_provider && `${currentUser?.id}` === `${data?.nurse_id}`;

        if (!isCurrentNurse) {
            return;
        }

        baseApiHelper().invalidateTags(["NurseLocations", "NursingHomeLocation", "Locations"]);
        fetchNursingLocations();

        try {
            const response = await getNurseLocations({
                response: "detailed",
                active: 1,
                all: 1,
                page: 1,
                start: 0,
                limit: 1,
            }, false);
            const nurseLocations = response?.data?.data || [];
            updateStoredAuthData({ is_facility_admin: !isAdminRevoked || nurseLocations.length > 0 });
            if (isAdminRevoked) {
                window.location.reload();
            }
        } catch (error) {
            console.error("Error syncing facility admin status", error);
        }
    }

    useEffect(() => {
        if(channelNames?.length){
            try {
                const uniqueChannelNames = [...new Set(channelNames.filter(Boolean))];
                const pusher = getPusher();
                if (!pusher) {
                    return;
                }

                let channels = uniqueChannelNames.map(channelName => pusher.subscribe(channelName));
                channels?.forEach((channel)=>{
                    channel.bind("nursing_home_chat", function (data) {
                        console.warn(":PUSHER RECEIVED ::", data);
                        if(data?.type == 'user_logged_out'){
                            toast.warn('Your session has expired, you will be redirected to Login..');
                            setTimeout(() => {
                                window.location = '/auth/login';
                            }, 5000);
                        }
                        else if (data?.type === "nursing_chat_questions_updated") {
                            if (questionUpdateTimeout.current) {
                                clearTimeout(questionUpdateTimeout.current);
                            }
                            questionUpdateTimeout.current = setTimeout(() => {
                                baseApiHelper().invalidateTags(["ProblemQuestions"]);
                                questionUpdateTimeout.current = null;
                            }, 3000);
                        } 
                        else if(notifyTypes.includes(data?.type)){
                            baseApiHelper().invalidateTags(["TaggedProvider"]);
                            if(data?.type == 'checkin' || data?.type == 'checkout'){
                                baseApiHelper().invalidateTags(["PracticeLocation"]);
                                if(!isPatientChatMessage(selectedChatRef.current)){
                                    baseApiHelper().invalidateTags(["providerLocationChats"]);
                                }
                            }
                        } else if (data?.type === "marked_acknowledged") {
                            if (acknowledgeTimeout.current) {
                                clearTimeout(acknowledgeTimeout.current);
                            }
                            acknowledgeTimeout.current = setTimeout(() => {
                                // baseApiHelper().updatePatientChatsCacheThroughPusher(data);
                                setMessages((prevMessages) => {
                                    try {
                                        const foundIndex = prevMessages?.data?.findIndex((msg) => `${msg?.id}` === `${data?.message_id}`);
                                        if (foundIndex > -1) {
                                            const newData = [...prevMessages.data];
                                            newData[foundIndex] = {
                                                ...newData[foundIndex],
                                                ...data,
                                                acknowledged_by_user_id: data.user,
                                            };
                                            const newMessages = { ...prevMessages, data: newData };
                                            return newMessages;
                                        }
                                        return prevMessages;
                                    } catch (error) {
                                        console.error("Error in set Messages:", error);
                                        return prevMessages;
                                    }
                                });
                                acknowledgeTimeout.current = null;
                            }, 1500);
                        }
                        else if(data?.type === "patient_location_changed"){
                            onUpdatePatientLocation(data)
                        }
                        else if(data?.type === "facility_admin_status_updated"){
                            syncFacilityAdminStatusUpdate(data);
                        }
                        else {
                            tryShowNotificationForMessage(data);
                            updateOpenThreadWithPusherMessage(data);
                            clearOpenChatMessageCount(data);
                            setPusherData(data);
                        }
                    });

                    channel.bind("my_event", function (data) {
                        console.warn("My event PUSHER RECEIVED ::", data);
                        if(["telemed_call_ended", "telemed_call_finished", "telemed_call_joined", "telemed_call_on_hold", "telemed_call_started", "telemed_call_left", "telemed_visitor_added_data", "telemed_user_added_to_room", "telemed_call_checked_in", "telemed_call_checked_out", "telemed_call_cancelled"].includes(data?.msgtype)){
                            if(data?.msgtype == "telemed_call_left" || data?.msgtype == "telemed_call_finished"){
                                setTelemedPusherData({...data, isCloseCallWindow: true});
                            }
                            else{
                                setTelemedPusherData(data);
                            }
                            baseApiHelper().invalidateTags(["vezoSessionsListing", "vezoSessionsWaitingList"]);
                        }
                        else if (["share_data_preparing","share_data_prepared"].includes(data?.msgtype)){
                            baseApiHelper().invalidateTags(["ShareData"]);
                        }
                    });
                })
                // channel = pusher.subscribe(authData?.is_provider ?  authData?.uuid : authData?.location_uuid);
                subscribedChannelNamesRef.current = uniqueChannelNames;

                return () => {
                    uniqueChannelNames?.forEach((channelName) => {
                        const channel = pusher.channel(channelName);
                        channel?.unbind();
                        pusher.unsubscribe(channelName);
                    });
                    subscribedChannelNamesRef.current = [];
                }
            } catch (error) {
                console.log(error)
            }
        }
    }, [channelNames]);

    useEffect(()=>{
        if(pusherData && pusherData.type != 'checkin' && pusherData.type != 'checkout'){
            localStoreUpdateForRecentChat();
        }
    }, [pusherData])
    
    const localStoreUpdateForRecentChat = () => {
        if(!pusherData?.message){
            return;
        }
        
        const newMsg = {id: pusherData.id, patient: pusherData.patient, pid: pusherData.pid, location_id: pusherData.location_id, message: pusherData.message,
            nurse_id: pusherData.nurse_id, user_id: pusherData.user_id, created: pusherData.created, last_message_at: pusherData.last_message_at || pusherData.created_at, location: pusherData.location,
            mentions: pusherData.mentions, urgent: pusherData.urgent
        };
        
        if(isPatientChatMessage(newMsg)){
            const selectedLocationId = selectedChatRef.current?.location_id || selectedChatRef.current?.location?.id;
            const messageLocationId = newMsg.location_id || newMsg.location?.id;
            const isCurrentPatientChat = selectedChatTabRef.current === 'patient_chats' && selectedChatRef.current?.pid == newMsg.pid && selectedLocationId == messageLocationId;
            if(!isCurrentPatientChat){
                newMsg.isMsgFromDifferentThread = true;
            }
            else{
                newMsg.isMsgFromDifferentThread = false;
                newMsg.unread_message_count = 0;
                newMsg.unread_urgent_count = 0;
                newMsg.unread_mention_count = 0;
                newMsg.patient = selectedChatRef.current?.patient;
            }
            if(shouldSuppressUnreadCount(newMsg) && !isCurrentPatientChat){
                newMsg.isMsgFromDifferentThread = false;
                delete newMsg.unread_message_count;
                delete newMsg.unread_urgent_count;
                delete newMsg.unread_mention_count;
            }

            baseApiHelper().updatePatientChatCacheThroughPusher(newMsg, selectedLocations);
        }
        else{
            const selectedLocationId = selectedChatRef.current?.location_id || selectedChatRef.current?.location?.id;
            const messageLocationId = newMsg.location_id || newMsg.location?.id;
            if(selectedChatTabRef.current !== 'location_chats' || selectedLocationId != messageLocationId){
                newMsg.isMsgFromDifferentThread = true;
            }
            const isCurrentLocationChat = selectedChatTabRef.current == 'location_chats' && selectedLocationId == messageLocationId;
            const isMessageFromLoggedInUser = isLoggedInUser(authDataRef.current, newMsg);
            newMsg.isMsgFromDifferentThread = !isCurrentLocationChat && !isMessageFromLoggedInUser;
            if(isCurrentLocationChat){
                newMsg.unread_message_count = 0;
                newMsg.unread_urgent_count = 0;
                newMsg.unread_mention_count = 0;
            }
            if(shouldSuppressUnreadCount(newMsg) && !isCurrentLocationChat){
                newMsg.isMsgFromDifferentThread = false;
                delete newMsg.unread_message_count;
                delete newMsg.unread_urgent_count;
                delete newMsg.unread_mention_count;
            }

            // don't update the chat if the message is a broadcast message and the user is logged in
            if(!(newMsg.message?.event_type == 'broadcast_message' && isMessageFromLoggedInUser)){
                const locationChatMsg = {...newMsg, pid: null};
                baseApiHelper().updateLocationChatCacheThroughPusher(locationChatMsg, selectedLocations);
            }
        }

        if(!shouldSuppressUnreadCount(newMsg)){
            baseApiHelper().invalidateTags(["UnreadMessageCount"]);
        }
    }

    const defaultValues = {
        pusher: pusherRef.current,
        pusherData,
        setPusherData,
        telemedPusherData,
        setTelemedPusherData
    };

    return (
        <PusherContext.Provider value={defaultValues}>
            {children}
        </PusherContext.Provider>
    );
}


export { PusherWrapper as default, usePusherContext, PusherContext};